import { TEST_IDS } from '@va/constants';
import { CheckmarkIcon, SquareIcon, StarIcon, VideoCameraIcon } from '@va/icons';
import { useLocale, useTranslate } from '@va/localization';
import { Paragraph, fontWeights, paragraphSizes } from '@va/ui/design-system';
import { TooltipWrapper } from '@va/ui/tooltips';
import { getFormattedDateAndTime } from '@va/util/helpers';
import { useIsFocused, useIsHovering } from '@va/util/hooks';
import classNames from 'classnames';
import React, { useMemo, useRef } from 'react';
import { useMarkSeenRecording } from '../apiClient';
import { useRecordingsContext } from '../context';
import { RecordingNoteIcon } from '../RecordingNoteIcon';
import { useIsHoveringOnTableRow } from '../useIsHoveringOnTableRow';

type IdAndDateProp = {
  recordingId: string;
  seen: boolean;
  star: boolean;
  date: number;
  note?: string;
  iconColor?: string;
  iconWrapperClassName?: string;
  size?: 'small' | 'medium';
  textClassName?: string;
  secondaryTextClassName?: string;
  onTitleClick?: (recordingId: string, event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
};

export const IdAndDateCell: React.FC<IdAndDateProp> = ({
  recordingId,
  seen,
  date,
  star,
  note,
  iconWrapperClassName = '',
  iconColor,
  size = 'medium',
  secondaryTextClassName,
  textClassName,
  onTitleClick,
}) => {
  const translate = useTranslate();
  const { locale } = useLocale();
  const cellRef = useRef<HTMLDivElement>(null);
  const selectButtonRef = useRef<HTMLButtonElement>(null);

  const { selectRecording, recordings, isRecordingSelected, deselectRecording } = useRecordingsContext();

  const { execute: markSeen } = useMarkSeenRecording([recordingId]);

  const isHoveringOnButton = useIsHovering(selectButtonRef);
  const isButtonFocused = useIsFocused(selectButtonRef);
  const isHoveringOnTableRow = useIsHoveringOnTableRow(cellRef);

  const icon = useMemo(() => {
    if (isRecordingSelected(recordingId))
      return (
        <CheckmarkIcon
          className={classNames({
            'w-4': size === 'small',
          })}
          color={iconColor}
        />
      );

    if (isHoveringOnButton || isButtonFocused)
      return (
        <SquareIcon
          className={classNames({
            'w-4': size === 'small',
          })}
          color={iconColor}
        />
      );

    return (
      <VideoCameraIcon
        className={classNames({
          'w-4': size === 'small',
        })}
        color={iconColor}
      />
    );
  }, [iconColor, isHoveringOnButton, isButtonFocused, isRecordingSelected, recordingId, size]);

  const paragraphPlaceholderMarginClass = useMemo(() => {
    if (star && note) return '';
    if (!star && note) return 'mr-3';
    if (!note && star) return 'mr-3';
    return 'mr-6';
  }, [note, star]);

  return (
    <div ref={cellRef} className='flex items-center w-full justify-start gap-2'>
      <TooltipWrapper content={translate('button.selectItem')} open={isButtonFocused || undefined}>
        <button
          ref={selectButtonRef}
          className={classNames(
            'flex items-center justify-center shrink-0 overflow-hidden rounded-full group-hover:bg-white ',
            {
              'w-12 h-12': size === 'medium',
              'w-9 h-9': size === 'small',
              'bg-white': isHoveringOnTableRow || isHoveringOnButton || isButtonFocused,
              'bg-white-snow': !isHoveringOnTableRow && !isHoveringOnButton && !isButtonFocused,
              [iconWrapperClassName]: !isHoveringOnTableRow,
            },
          )}
          onClick={(event) => {
            event.stopPropagation();

            if (isRecordingSelected(recordingId)) {
              deselectRecording(recordingId);
            } else {
              selectRecording(recordings.find((recording) => recording.id === recordingId)?.id ?? '');
            }
          }}
        >
          {icon}
        </button>
      </TooltipWrapper>
      <button
        className='text-left grow overflow-hidden cursor-pointer'
        onClick={(event) => {
          if (!seen) {
            markSeen(true);
          }

          onTitleClick?.(recordingId, event);
        }}
      >
        <div className='flex items-center justify-start gap-1'>
          {star && <StarIcon data-testid={TEST_IDS.generic.recordings.starIcon} color='#F66F1E' />}
          {note && <RecordingNoteIcon note={note} />}
          <TooltipWrapper content={recordingId.toUpperCase()}>
            <Paragraph
              size={size === 'medium' ? paragraphSizes.tiny : paragraphSizes.normal}
              weight={fontWeights.medium}
              className={classNames('w-full truncate', paragraphPlaceholderMarginClass, textClassName, {
                'leading-18!': size === 'small',
              })}
            >
              {recordingId.toUpperCase()}
            </Paragraph>
          </TooltipWrapper>
        </div>
        <Paragraph
          className={secondaryTextClassName}
          colorClassName='text-text-secondary'
          size={paragraphSizes.tiny}
          weight={fontWeights.medium}
        >
          {getFormattedDateAndTime(date, locale)}
        </Paragraph>
      </button>
    </div>
  );
};
